import dayjs from 'dayjs';
import { fromPairs, isNil, map, pipe } from 'ramda';

import { PremiumPay, PremiumPayFlagsEnum } from '../../../types';
import { FlagField, PremiumPayFormValues, PremiumPayForSave } from '../types';
import { CLAOption } from 'types';

import { isSvcTypeBonus } from '../../../utils/utils';
import { includes } from 'lodash';

const mapFormFlagsForApi = (formFlagsData: FlagField[]) => {
  const flagsArr: PremiumPayFlagsEnum[] = [];
  for (const flag in formFlagsData) {
    if (formFlagsData[flag]) {
      flagsArr.push(PremiumPayFlagsEnum[flag]);
    }
  }
  return flagsArr;
};

export const convertResponseToForm = (
  data: PremiumPay
): PremiumPayFormValues => {
  return {
    ...data,
    bonusTypeSvcName: data.bonusTypeSvc ?? '',
    bonusTypeSvc: {
      label: data.bonusTypeSvc,
      value: data.bonusTypeSvc
    },
    valid: {
      from: new Date(data.valid.from),
      to: new Date(data.valid.to)
    },
    timeFrom: dayjs(data.timeFrom),
    timeTo: dayjs(data.timeTo),
    timeFromThr: !isNil(data?.timeFromThr)
      ? dayjs(data.timeFromThr)
      : undefined,
    timeToThr: !isNil(data?.timeToThr) ? dayjs(data.timeToThr) : undefined,
    flags: pipe(
      map<string, [string, boolean]>((value) => [value, true]),
      fromPairs
    )(data.flags) as unknown as FlagField[],
    scPri: data.scPri ?? '',
    scSec: data.scSec ?? ''
  };
};

export const convertPayloadForSave = (
  data: PremiumPayFormValues,
  premiumPayTypes: CLAOption[] | undefined
): PremiumPayForSave => {
  const flags = mapFormFlagsForApi(data.flags);
  const notIncludedDayFlags = Object.values(PremiumPayFlagsEnum).filter(
    (day) => !includes(flags, day)
  );
  const rootFlags: Record<string, boolean> = {
    ...Object.fromEntries(flags.map((flag) => [flag, true])),
    ...Object.fromEntries(notIncludedDayFlags.map((flag) => [flag, false]))
  };

  return {
    percentage: data.percentage,
    columnNr: data.columnNr,
    bonusTypeSvc: data?.bonusTypeSvc?.value,
    valid: {
      from: data.valid.from?.toISOString(),
      to: data.valid.to?.toISOString()
    },
    flags: mapFormFlagsForApi(data.flags),
    timeFrom: data.timeFrom.toISOString(),
    timeTo:
      data.timeTo < data.timeFrom
        ? dayjs(data.timeTo).add(1, 'day').toISOString()
        : data.timeTo.toISOString(),
    claId:
      premiumPayTypes &&
      premiumPayTypes.filter((item) => item.code == data.claCode)[0].claId,
    ...(data?.claBonusId && {
      claBonusId: data.claBonusId
    }),
    ...(mapFormFlagsForApi(data.flags).includes(
      PremiumPayFlagsEnum.TIME_FROM_THR
    ) && data?.timeFromThr
      ? {
          timeFromThr: data.timeFromThr.toISOString()
        }
      : { timeFromThr: null }),
    ...(mapFormFlagsForApi(data.flags).includes(
      PremiumPayFlagsEnum.TIME_TO_THR
    ) && data?.timeToThr
      ? {
          timeToThr:
            data?.timeFromThr && data?.timeToThr < data?.timeFromThr
              ? dayjs(data.timeToThr).add(1, 'day').toISOString()
              : data.timeToThr.toISOString()
        }
      : { timeToThr: null }),
    ...(data?.maxHoursPerDayrange && {
      maxHoursPerDayrange: data.maxHoursPerDayrange
    }),
    bonus25Method:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.bonus25Method
        ? data.bonus25Method
        : null,
    scPri: data.scPri,
    scSec: data.scSec,
    maxHoursPerDayrange:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.maxHoursPerDayrange
        ? data.maxHoursPerDayrange
        : 0,
    dayrange:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.dayrange
        ? data.dayrange
        : null,
    ...rootFlags
  };
};
